import { Box, Button, CircularProgress, Divider, IconButton, MenuItem, Stack, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxTextField from "src/components/inputs/trux-text-field";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { DeviceWithMapInfo, RentalGpsDevice, RentalTrailer, RentalTrailerEvent } from "src/models";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import { Constants, RENTAL_TRAILER_TYPES, isRentalTrailerRentedOut } from "src/constants";
import TruxSelectField from "src/components/inputs/trux-select-field";
import { RentalFileField } from "./rental-file-field";

type RentalTrailerModalProps = {
  selectedRentalTrailerModel: ModelSavingData<RentalTrailer>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateRentalTrailer: VoidFunction,
  devices?: RentalGpsDevice[],
  rentalTrailers?: RentalTrailer[],
  events?: RentalTrailerEvent[],
  truck?: boolean,
}

export function deviceUnitNumber(device: DeviceWithMapInfo) {
  if (device.deviceType.startsWith("Trailer ")) {
    return device.deviceType.substring("Trailer ".length);
  }
  if (device.deviceType.startsWith("ELD Truck #")) {
    return device.deviceType.substring("ELD Truck #".length);
  }
  return device.deviceType;
}

export function gpsDeviceLabel(device: RentalGpsDevice) {
  return device.organizationName ? `${device.organizationName} - ${device.deviceType}` : device.deviceType;
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Stack>
      <Typography variant="subtitle2">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Stack>
  );
}

function DocumentField({ label, fileUrl, onChange }: { label: string; fileUrl: string; onChange: (fileUrl: string) => void }) {
  return (
    <Stack gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <RentalFileField fileUrl={fileUrl} onChange={onChange} />
    </Stack>
  );
}

export function RentalTrailerModal(
  { selectedRentalTrailerModel, isOpen, setIsOpen, updateRentalTrailer, devices, rentalTrailers, events, truck }: RentalTrailerModalProps
) {
  const unitLabel = truck ? "Truck" : "Trailer";
  const typeOptions = RENTAL_TRAILER_TYPES.filter((trailerType) => !!trailerType.truck === !!truck);
  const isRentedOut = isRentalTrailerRentedOut(selectedRentalTrailerModel.model?.id, events);

  const [loadingVin, setLoadingVin] = useState(false);

  const decodeVin = () => {
    setLoadingVin(true);
    const rentalTrailer = selectedRentalTrailerModel.model;
    if (rentalTrailer) {
      if (!rentalTrailer.vin) {
        enqueueSnackbar("Please enter a vin", { variant: "error" });
        return;
      }

      fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${encodeURIComponent(rentalTrailer.vin)}?format=json`).then(response => response.json()).then((response) => {
        let make = "";
        let model = "";

        response.Results.forEach((result: { Variable: string; Value: string }) => {
          if (result.Variable === "Make") {
            make = result.Value || "";
          } else if (result.Variable === "Model") {
            model = result.Value || "";
          }
        });
        if (make || model) {
          selectedRentalTrailerModel.update(() => {
            rentalTrailer.make = make;
            rentalTrailer.model = model;
          })
        } else {
          enqueueSnackbar("Make, Model and/or Year could not be decoded", { variant: "error" });
        }
      }).finally(() => {
        setLoadingVin(false);
      })
    }
  }

  return selectedRentalTrailerModel.model && <SimpleDialog
    title={<>{selectedRentalTrailerModel.isNew ? `New ${unitLabel}` : `Update ${unitLabel}`}</>}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={updateRentalTrailer}
    submitDisabled={selectedRentalTrailerModel.loading}
    submitButton={selectedRentalTrailerModel.isNew ? `New ${unitLabel}` : `Update ${unitLabel}`}
    submitButtonIcon={selectedRentalTrailerModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
    submitButtonColor={selectedRentalTrailerModel.isNew ? "success" : "info"}>


    <DisplayErrors savingData={selectedRentalTrailerModel} />

    <Stack direction="column" gap={2}>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {(devices?.length || 0) > 0 && (() => {
          const otherTrailers = (rentalTrailers || [])
            .filter(trailer => trailer.id !== selectedRentalTrailerModel.model?.id);
          const otherTrailerUnits = new Set(otherTrailers.map(trailer => trailer.trailerUnitNumber));
          const otherTrailerDeviceIds = new Set(otherTrailers.map(trailer => trailer.deviceId).filter(Boolean));
          const deviceOptions = devices!
            .filter(device => !otherTrailerDeviceIds.has(device.id) && !otherTrailerUnits.has(deviceUnitNumber(device)))
            .map(device => ({ id: device.id, label: gpsDeviceLabel(device) }));
          // directly-linked device wins; unit-number match covers legacy trailers
          const selectedDevice =
            devices!.find(device => device.id === selectedRentalTrailerModel.model?.deviceId) ||
            devices!.find(device => deviceUnitNumber(device) === selectedRentalTrailerModel.model?.trailerUnitNumber);

          return <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
            <TruxAutocompleteField
              label="GPS Device"
              options={deviceOptions}
              selected={selectedDevice ? { id: selectedDevice.id, label: gpsDeviceLabel(selectedDevice) } : { id: 0, label: "" }}
              freeSolo={false}
              noOptionsText="No GPS devices found"
              onChange={(selection) => {
                if (!selection) {
                  return;
                }
                const device = devices!.find(d => d.id === selection.id);
                if (device) {
                  selectedRentalTrailerModel.update(trailer => {
                    trailer.deviceId = device.id;
                    trailer.trailerUnitNumber = deviceUnitNumber(device);
                  });
                }
              }}
              onClear={() => {
                selectedRentalTrailerModel.update(trailer => {
                  trailer.deviceId = 0;
                });
              }}
            />
          </Box>;
        })()}

        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="trailerUnitNumber" label="Unit Number *" />

        <TruxSelectField modelSavingData={selectedRentalTrailerModel} field="trailerType" label={`${unitLabel} Type *`}>
          {typeOptions.map((trailerType) => <MenuItem value={trailerType.value} key={trailerType.value}>{trailerType.label}</MenuItem>)}
        </TruxSelectField>

        <Box sx={{ gridColumn: { sm: "1 / -1" }, display: "flex", gap: 1.5 }}>
          <Box flex={1}>
            <TruxTextField modelSavingData={selectedRentalTrailerModel} field="vin" label="VIN *" />
          </Box>
          <Button variant="soft" color="info" onClick={decodeVin} sx={{ flexShrink: 0 }}>
            {loadingVin && <CircularProgress size={16} sx={{ marginRight: "8px" }} />} Decode
          </Button>
        </Box>

        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="make" label="Make" />
        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="model" label="Model" />
        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="year" label="Year" />

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box flex={1.5}>
            <TruxTextField modelSavingData={selectedRentalTrailerModel} field="licensePlate" label="License Plate *" />
          </Box>
          <Box flex={1}>
            <TruxSelectField modelSavingData={selectedRentalTrailerModel} field="licensePlateState" label="State">
              <MenuItem value="">-</MenuItem>
              {Constants.STATES.map((state) => <MenuItem value={state} key={state}>{state}</MenuItem>)}
            </TruxSelectField>
          </Box>
        </Box>
      </Box>

      <Divider />
      <SectionHeader title="Documents" />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <DocumentField
          label="Registration"
          fileUrl={selectedRentalTrailerModel.model.registrationFile}
          onChange={(fileUrl) => selectedRentalTrailerModel.update(t => { t.registrationFile = fileUrl; })} />
        <DocumentField
          label="Insurance"
          fileUrl={selectedRentalTrailerModel.model.insuranceFile}
          onChange={(fileUrl) => selectedRentalTrailerModel.update(t => { t.insuranceFile = fileUrl; })} />
        <DocumentField
          label="DOT Inspection"
          fileUrl={selectedRentalTrailerModel.model.inspectionFile}
          onChange={(fileUrl) => selectedRentalTrailerModel.update(t => { t.inspectionFile = fileUrl; })} />
        <DocumentField
          label="Liability Insurance"
          fileUrl={selectedRentalTrailerModel.model.liabilityInsuranceFile}
          onChange={(fileUrl) => selectedRentalTrailerModel.update(t => { t.liabilityInsuranceFile = fileUrl; })} />
        <DocumentField
          label={`${unitLabel} Photo`}
          fileUrl={selectedRentalTrailerModel.model.photoUrl}
          onChange={(fileUrl) => selectedRentalTrailerModel.update(t => { t.photoUrl = fileUrl; })} />
      </Box>

      <Divider />
      <SectionHeader
        title="Technical Work"
        subtitle="Anything done on the unit; attach the invoice or work order PDF if you have it"
      />

      <Stack gap={1.5}>
        {(selectedRentalTrailerModel.model.workLog || []).map((entry, index) => (
          <Stack key={index} direction="row" gap={1.5} alignItems="center">
            <Box width={130} flexShrink={0}>
              <TruxSimpleTextField
                label="Date"
                value={entry.date}
                onChange={(date) => selectedRentalTrailerModel.update(t => {
                  t.workLog = (t.workLog || []).map((line, i) => i === index ? { ...line, date } : line);
                })} />
            </Box>
            <Box flex={1}>
              <TruxSimpleTextField
                label="What was done"
                value={entry.description}
                onChange={(description) => selectedRentalTrailerModel.update(t => {
                  t.workLog = (t.workLog || []).map((line, i) => i === index ? { ...line, description } : line);
                })} />
            </Box>
            <RentalFileField
              fileUrl={entry.fileUrl || ""}
              onChange={(fileUrl) => selectedRentalTrailerModel.update(t => {
                t.workLog = (t.workLog || []).map((line, i) => i === index ? { ...line, fileUrl } : line);
              })} />
            <IconButton
              size="small"
              color="error"
              onClick={() => selectedRentalTrailerModel.update(t => {
                t.workLog = (t.workLog || []).filter((_line, i) => i !== index);
              })}>
              <Iconify icon="solar:trash-bin-trash-bold" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button
            size="small"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={() => selectedRentalTrailerModel.update(t => {
              t.workLog = [...(t.workLog || []), { date: DateTime.now().toFormat("MM/dd/yyyy"), description: "", fileUrl: "" }];
            })}>
            Add Work Entry
          </Button>
        </Box>
      </Stack>

      <Divider />

      <Stack direction="row" alignItems="center" gap={1}>
        <TruxCheckboxField modelSavingData={selectedRentalTrailerModel} field="active" disabled={isRentedOut} />
        <Typography variant="body2">Active</Typography>
        {isRentedOut && (
          <Typography variant="caption" color="text.secondary">
            This {unitLabel.toLowerCase()} is currently rented out and must be returned before it can be deactivated.
          </Typography>
        )}
      </Stack>
    </Stack>
  </SimpleDialog >
}
